import { Link } from "react-router-dom";
import { ArrowRight, CalendarCheck, CheckCircle2, ClipboardList, Home as HomeIcon, Ruler, Scissors, Wrench } from "lucide-react";
import { site } from "@/config/site";
import { useLeadForm } from "@/components/lead/LeadForm";
import { SEO, breadcrumbLd, faqLd } from "@/components/site/SEO";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { FAQ } from "@/components/site/FAQ";
import { CTASection } from "@/components/site/CTASection";

const measureSteps = [
  { icon: CalendarCheck, title: "You request an appointment", text: "Book online, call or WhatsApp — pick a time that suits you, including Saturdays by appointment." },
  { icon: HomeIcon, title: "We visit your property", text: "A specialist comes to you with real samples, so you can see fabrics and finishes in your own light." },
  { icon: Ruler, title: "Windows measured properly", text: "We measure every window precisely — recess depth, handles, tiles and bay angles all accounted for." },
  { icon: Scissors, title: "You choose products & materials", text: "Compare styles side by side with honest advice. No pressure, no pushy selling." },
  { icon: ClipboardList, title: "Your quote is prepared", text: "You receive a clear, written, no-obligation quotation for exactly what you chose." },
  { icon: Wrench, title: "Fitted professionally", text: "If you're happy to go ahead, your made-to-measure order is fitted by our own experienced fitters." },
];

const faqs = [
  {
    q: "Is the home measure really free?",
    a: "Yes — the visit, the measuring and the written quotation are all completely free, with no obligation to order.",
  },
  {
    q: "How long does a home visit take?",
    a: "Most visits take 30–60 minutes depending on how many windows you'd like measured and how much advice you'd like. We never rush you.",
  },
  {
    q: "Do I need to decide on the day?",
    a: "Not at all. Your written quote is yours to keep and think over. We're here when you're ready — no pressure calls.",
  },
  {
    q: "Which areas do you cover for home measures?",
    a: "Birmingham and the wider West Midlands, including Solihull, Sutton Coldfield, Walsall, West Bromwich, Dudley and Halesowen. Send your postcode if you'd like to check.",
  },
];

export default function FreeHomeMeasure() {
  const { openForm } = useLeadForm();
  return (
    <>
      <SEO
        title="Free Home Measure Birmingham | Blinds Measured & Quoted | My Blinds Company"
        description="Book your free blinds home measure in Birmingham. We visit with samples, measure every window professionally and leave you a clear, no-obligation quote."
        path="/free-home-measure"
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Free Home Measure", path: "/free-home-measure" },
          ]),
          faqLd(faqs),
        ]}
      />

      {/* HERO */}
      <section className="relative bg-navy-950 text-white overflow-hidden grain" data-testid="fhm-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <Reveal>
            <Eyebrow className="text-white/60">Free Measure & Quote</Eyebrow>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08]">
              Book Your Free Blinds Home Measure in Birmingham
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-white/75 max-w-2xl">
              Professionally measured. Made to fit. Expertly installed. One free
              visit covers everything — samples, measuring, honest advice and a
              clear written quote with no obligation.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                data-testid="fhm-book-btn"
                onClick={() => openForm({ source: "free_home_measure" })}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold text-navy-900 shadow-2xl transition-all duration-300 hover:-translate-y-0.5"
              >
                Book My Free Home Visit <ArrowRight className="h-4 w-4" />
              </button>
              <a
                href={site.phone.href}
                data-testid="fhm-call-btn"
                className="inline-flex items-center justify-center rounded-full border border-white/40 bg-white/5 backdrop-blur px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-white hover:text-navy-900"
              >
                Call {site.phone.display}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* STEPS */}
      <section className="py-16 sm:py-24" data-testid="fhm-steps">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-12">
            <Eyebrow>How It Works</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              What happens at your free home measure
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {measureSteps.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-warm-200 bg-white p-7 shadow-sm hover:shadow-lg transition-shadow duration-300">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-navy-900 mb-5">
                    <s.icon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="font-sans text-lg font-semibold text-slate-900 mb-2">{s.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-600">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY PROFESSIONAL MEASURING */}
      <section className="py-16 sm:py-24 bg-warm-100/60">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>Why It Matters</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              Why professional measuring beats the tape measure
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-slate-600">
              <p>
                A blind that's 5mm out will catch, gap or refuse to sit straight.
                Measuring for made-to-measure products means accounting for the
                things a quick DIY measure misses — out-of-square reveals,
                handle clearance, tile returns and the angles of a bay.
              </p>
              <p>
                Our specialists measure every window the way the factory needs
                it, so what arrives fits first time. It's the biggest difference
                between ordering online and having it done properly.
              </p>
            </div>
            <ul className="mt-8 grid sm:grid-cols-2 gap-3">
              {["Free visit, measuring and written quote", "Real samples in your own home", "No obligation and no pressure", "Evening and Saturday appointments"].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> {t}
                </li>
              ))}
            </ul>
            <button
              type="button"
              data-testid="fhm-book-btn-2"
              onClick={() => openForm({ source: "free_home_measure_mid" })}
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-navy-900 px-8 py-4 text-sm font-semibold text-white shadow-lg transition-all duration-300 hover:bg-navy-800 hover:-translate-y-0.5"
            >
              Book My Free Home Visit <ArrowRight className="h-4 w-4" />
            </button>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-10">
            <Eyebrow className="justify-center">Questions</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              Free home measure FAQs
            </h2>
          </Reveal>
          <FAQ items={faqs} idPrefix="fhm-faq" />
          <Reveal className="mt-10 text-center text-sm text-slate-600">
            Looking for a specific product? See our <Link to="/blinds" className="font-semibold text-navy-900 hover:underline">full blinds range</Link> or{" "}
            <Link to="/shutters" className="font-semibold text-navy-900 hover:underline">made-to-measure shutters</Link>.
          </Reveal>
        </div>
      </section>

      <CTASection source="free_home_measure_final" />
    </>
  );
}
